import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppTile } from "../components/AppTile";
import { useApi } from "../hooks/useApi";
import { type ApiApp, ApiError, api } from "../lib/api";
import { redirectToLogin } from "../lib/auth-redirect";

type AppFilter = "all" | "mine" | "favorites";

export interface LaunchpadProps {
  /** Nutzer darf jede App verwalten (Admin/Operator) — Zahnrad dann auf allen Kacheln. */
  canManageAll?: boolean;
  /** Öffnet die Verwaltungs-/Detailseite einer App (Zahnrad); optional auf einem
   *  bestimmten Tab (#6: Teilen-Icon springt direkt auf den Teilen-Tab). */
  onManageApp?: (appId: string, tab?: "share") => void;
  /** #8: Sharing-UI (Kachel-Icon) nur im Showroom-Modus zeigen. */
  sharingEnabled?: boolean;
}

export function Launchpad({
  canManageAll = false,
  onManageApp,
  sharingEnabled = false,
}: LaunchpadProps): JSX.Element {
  const { t } = useTranslation();
  const apps = useApi(() => api.apps(), []);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<AppFilter>("all");
  // Optimistischer Favoriten-Override: appId -> bool, überschreibt app.is_favorite
  // bis zum nächsten Reload.
  const [favOverride, setFavOverride] = useState<Record<string, boolean>>({});

  // 401 -> Login. Der Redirect gehoert in einen Effekt (nicht in den
  // Render-Body): sonst feuert er bei jedem Re-Render erneut und ueberschreibt
  // den OIDC-Handshake-Cookie mit immer neuem state ("unexpected state"-Bug).
  // Der Modul-Guard in redirectToLogin() haelt es zusaetzlich single-flight.
  const appsUnauthorized =
    apps.status === "error" && apps.error instanceof ApiError && apps.error.status === 401;
  useEffect(() => {
    if (appsUnauthorized) redirectToLogin();
  }, [appsUnauthorized]);

  const isFav = (a: ApiApp): boolean => favOverride[a.id] ?? a.is_favorite;

  const reload = apps.reload;

  const toggleFavorite = async (a: ApiApp): Promise<void> => {
    const next = !isFav(a);
    setFavOverride((m) => ({ ...m, [a.id]: next }));
    try {
      if (next) await api.favorite(a.id);
      else await api.unfavorite(a.id);
    } catch {
      setFavOverride((m) => ({ ...m, [a.id]: !next })); // rollback
    }
  };

  const canManage = (a: ApiApp): boolean => canManageAll || a.is_owner;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `isFav` closes over `favOverride`, which is already a dependency.
  const filtered = useMemo(() => {
    if (apps.status !== "ready") return [];
    const needle = query.trim().toLowerCase();
    return apps.data.apps.filter((a) => {
      if (filter === "mine" && !a.is_owner) return false;
      if (filter === "favorites" && !isFav(a)) return false;
      if (!needle) return true;
      return (
        a.name.toLowerCase().includes(needle) ||
        a.slug.toLowerCase().includes(needle) ||
        (a.category?.toLowerCase().includes(needle) ?? false) ||
        (a.description?.toLowerCase().includes(needle) ?? false)
      );
    });
  }, [apps, query, filter, favOverride]);

  // #35: Status-Polling, solange eine sichtbare App in pulling/starting ist.
  useEffect(() => {
    if (apps.status !== "ready") return;
    const inFlight = apps.data.apps.some(
      (a) => a.runtime?.observed_state === "pulling" || a.runtime?.observed_state === "starting",
    );
    if (!inFlight) return;
    const handle = setInterval(() => reload(), 3000);
    return () => clearInterval(handle);
  }, [apps, reload]);

  if (apps.status === "loading") {
    return <p className="launchpad__hint">{t("launchpad.loading")}</p>;
  }

  if (apps.status === "error") {
    if (appsUnauthorized) {
      // Navigation passiert im useEffect oben (single-flight) — hier nur Hinweis.
      return <p className="launchpad__hint">{t("launchpad.redirecting")}</p>;
    }
    return (
      <p className="launchpad__hint launchpad__hint--error">
        {t("launchpad.errorPrefix")} {apps.error.message}
      </p>
    );
  }

  if (apps.data.apps.length === 0) {
    return <p className="launchpad__hint">{t("launchpad.empty")}</p>;
  }

  return (
    <section className="launchpad">
      <div className="launchpad__toolbar">
        <div className="launchpad__filters">
          {(["all", "mine", "favorites"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`launchpad__filter${filter === f ? " launchpad__filter--active" : ""}`}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {t(`launchpad.filter.${f}`)}
            </button>
          ))}
        </div>
        <div className="launchpad__search-wrap">
          <span className="launchpad__search-icon">
            <Search size={16} strokeWidth={2.5} aria-hidden="true" />
          </span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("launchpad.searchPlaceholder")}
            aria-label={t("launchpad.searchLabel")}
            className="launchpad__search"
          />
        </div>
        <span className="launchpad__count">
          {t("launchpad.appCount", { count: filtered.length })}
        </span>
      </div>
      {filtered.length === 0 ? (
        <p className="launchpad__hint">{t("launchpad.noMatch")}</p>
      ) : (
        <ul className="launchpad__grid">
          {filtered.map((app) => (
            <li key={app.id}>
              <AppTile
                app={app}
                isFavorite={isFav(app)}
                onToggleFavorite={() => toggleFavorite(app)}
                {...(onManageApp && canManage(app)
                  ? { onManage: (a: ApiApp) => onManageApp(a.id) }
                  : {})}
                {...(sharingEnabled && onManageApp && canManage(app) && app.shareable
                  ? {
                      onShare: () => onManageApp(app.id, "share"),
                      hasActiveShareLink: app.has_active_share_link,
                    }
                  : {})}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
